
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Shield, Ruler } from "lucide-react";
import { ParcelDetails } from "@/hooks/bookings/types";

interface BookingParcelCardProps {
  parcel: ParcelDetails;
  index: number;
}

const getParcelTypeLabel = (type: string, otherTypeSpecification: string | null) => {
  const typeLabels: Record<string, string> = {
    "documents": "Documents & Paperwork",
    "small-packages": "Small Packages & Personal Items",
    "fragile": "Fragile & Breakable Items",
    "food": "Food & Perishables",
    "medical": "Health & Medical Supplies",
    "large": "Large & Bulky Items",
    "special": "Special Handling Items",
    "other": `Other: ${otherTypeSpecification || 'Not specified'}`,
  };

  return typeLabels[type] || type;
};

const BookingParcelCard = ({ parcel, index }: BookingParcelCardProps) => {
  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            <h4 className="font-medium">Parcel {index + 1}</h4>
          </div>
          <div className="flex gap-1">
            <Badge variant="outline" className="text-xs">
              {parcel.quantity}x
            </Badge>
            {parcel.insurance_enabled && (
              <Badge variant="secondary" className="text-xs">
                <Shield className="h-3 w-3 mr-1" />
                Insured
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">Type</p>
          <p className="text-sm">{getParcelTypeLabel(parcel.parcel_type, parcel.other_type_specification)}</p>
        </div>
        
        {parcel.description && (
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Description</p>
            <p className="text-sm">{parcel.description}</p>
          </div>
        )}
        
        <div className="grid grid-cols-1 gap-4 pt-2 border-t border-border">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Weight (each)</p>
            <p className="text-sm font-medium">{parcel.weight || 'N/A'} kg</p>
          </div>
          
          {parcel.dimensions && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Ruler className="h-3 w-3 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground">Dimensions (each)</p>
              </div>
              <p className="text-sm">
                {parcel.dimensions.length} × {parcel.dimensions.width} × {parcel.dimensions.height} cm
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingParcelCard;
